import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Briefcase, Calendar, CheckCircle2, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface BuildWorkspace {
  id: string;
  idea_id: string;
  created_at: string;
  updated_at: string;
  ideas: {
    id: string;
    title: string;
    tagline: string | null;
    niche: string;
    tags: string[];
  };
  tasks: {
    total: number;
    completed: number;
  };
}

export const YourWorkspaces = ({ onWorkspaceSelect }: { onWorkspaceSelect: (ideaId: string) => void }) => {
  const { user } = useAuth();

  const { data: workspaces, isLoading } = useQuery({
    queryKey: ['build-workspaces', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Fetch build plans with their ideas - no limit, get all workspaces
      const { data: plans, error: plansError } = await supabase
        .from('build_plans')
        .select(`
          id,
          idea_id,
          created_at,
          updated_at,
          ideas (
            id,
            title,
            tagline,
            niche,
            tags
          )
        `)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (plansError) throw plansError;

      // Fetch task counts for each plan
      const workspacesWithProgress = await Promise.all(
        (plans || []).map(async (plan: any) => {
          const { data: tasks, error: tasksError } = await supabase
            .from('build_tasks')
            .select('status')
            .eq('plan_id', plan.id);

          if (tasksError) {
            console.error('Error fetching tasks:', tasksError);
            return {
              ...plan,
              tasks: { total: 0, completed: 0 }
            };
          }

          const total = tasks?.length || 0;
          const completed = tasks?.filter((t: any) => t.status === 'done').length || 0;

          return {
            ...plan,
            tasks: { total, completed }
          };
        })
      );

      return workspacesWithProgress as BuildWorkspace[];
    },
    enabled: !!user?.id,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="h-6 w-6 text-primary" />
            Your Workspaces
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Continue building your startup ideas
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full mb-4" />
                <Skeleton className="h-2 w-full mb-2" />
                <Skeleton className="h-9 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!workspaces || workspaces.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Briefcase className="h-6 w-6 text-primary" />
          Your Workspaces
        </h2>
        <p className="text-muted-foreground text-sm mt-1">
          Continue building your {workspaces.length} startup {workspaces.length === 1 ? 'idea' : 'ideas'}
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {workspaces.map((workspace) => {
          const idea = workspace.ideas;
          if (!idea) return null;

          const progressPercent = workspace.tasks.total > 0 
            ? Math.round((workspace.tasks.completed / workspace.tasks.total) * 100)
            : 0;

          return (
            <Card 
              key={workspace.id}
              className="hover:shadow-lg transition-all border-border/50 cursor-pointer"
              onClick={() => onWorkspaceSelect(idea.id)}
            >
              <CardContent className="p-6 space-y-4">
                <div className="space-y-2">
                  <h3 className="font-bold text-lg line-clamp-1 leading-tight">
                    {idea.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {idea.tagline || 'Building a 90-day plan'}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {idea.niche && (
                    <Badge variant="secondary" className="text-xs">
                      {idea.niche}
                    </Badge>
                  )}
                  {idea.tags?.slice(0, 2).map((tag: string) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">
                      {workspace.tasks.completed}/{workspace.tasks.total} tasks
                    </span>
                  </div>
                  <Progress value={progressPercent} className="h-2" />
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <CheckCircle2 className="h-3 w-3" />
                    <span>{progressPercent}% complete</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  <span>
                    Updated {new Date(workspace.updated_at).toLocaleDateString()}
                  </span>
                </div>

                <Button 
                  asChild
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Link to={`/build/${idea.id}`}>
                    Open Workspace
                  </Link>
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
